// src/context/ThemeContextProvider.js
import React, {
  createContext,
  useState,
  useMemo,
  useEffect,
  useCallback,
  useContext,
} from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import { getThemePreference, saveThemePreference } from '../graphql/queries';
import { useLazyQuery } from '@apollo/client';
import { getStyledTheme } from './styledTheme';
import { useTheme } from 'pepsico-ds';
import { useSelector } from 'react-redux';

export const ThemeContext = createContext();

export const useThemeContext = () => useContext(ThemeContext);

const ThemeContextProvider = ({ children }) => {
  const [mode, setMode] = useState('light');
  const [fetchThemePreference, { data: themeData }] =
    useLazyQuery(getThemePreference);
  const [saveThemePreferences] = useLazyQuery(saveThemePreference);
  const { setTheme } = useTheme();

  // Get MFE host config to check for theme mode
  const isMFE = useSelector((state) => state.app?.isMFE);
  const mfeHostConfig = useSelector((state) => state.app?.mfeHostConfig);

  // Convert mode from API values to local theme and vice versa.
  const convertMode = useCallback((inputMode) => {
    const mapping = {
      L: 'light',
      D: 'dark',
      light: 'L',
      dark: 'D',
    };
    return mapping[inputMode];
  }, []);

  const styledTheme = useMemo(() => getStyledTheme(mode), [mode]);

  // Effect for fetching theme preference from API (non-MFE mode)
  useEffect(() => {
    if (!isMFE) {
      fetchThemePreference();
    }
  }, [fetchThemePreference, isMFE]);

  // Effect for setting theme based on API response (non-MFE mode)
  useEffect(() => {
    if (!isMFE) {
      const apiMode = themeData?.getDashboardMode?.data?.mode;
      if (apiMode) {
        // Convert API mode to local mode (if conversion returns undefined, fallback to "light").
        setMode(convertMode(apiMode) || 'light');
      }
    }
  }, [themeData, convertMode, isMFE]);

  // Effect for MFE mode - set theme based on mfeHostConfig
  useEffect(() => {
    if (isMFE && mfeHostConfig?.themeMode) {
      setMode(mfeHostConfig.themeMode);
    }
  }, [isMFE, mfeHostConfig]);

  const toggleTheme = useCallback(() => {
    const newMode = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
    // In MFE mode, dispatch custom event to notify host
    if (isMFE) {
      window.dispatchEvent(
        new CustomEvent('themeToggled', { detail: newMode })
      );
    } else {
      // Save the theme preference with the converted value only in non-MFE mode
      saveThemePreferences({ variables: { mode: convertMode(newMode) } });
    }
  }, [mode, saveThemePreferences, convertMode, isMFE]);

  const isDark = mode === 'dark';

  useEffect(() => {
    setTheme(isDark ? 'Dark Peacock' : 'Light Peacock');
  }, [isDark]);

  const contextValue = useMemo(
    () => ({
      isDark,
      mode,
      toggleTheme,
      setMode,
      colors: styledTheme.colors,
    }),
    [isDark, mode, toggleTheme, setMode, styledTheme.colors]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <StyledThemeProvider theme={styledTheme}>{children}</StyledThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeContextProvider;
